import { GridParentFilter } from './../../../../../../../models/ts/grid-parent-filter.model';
import { Component, computed, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { BaseFormControlComponent } from '../../../classes/base-form-control.component';
import { FormCollectionListComponent } from '../../form-collection-list/form-collection-list.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CollectionListApiService } from 'src/app/api/bizzmine/collection-list/collection-list-api.service';
import { TableViewWidgetData } from 'src/models/ts/table-view-widget-data.model';
import { selectForm } from 'src/app/store/features/forms/forms-selectors';
import { refreshActions } from 'src/app/store/features/refresh/refresh-actions';
import { LoaderComponent } from '../../../../../../shared/components/ui/loader/loader.component';
import { map, Observable, shareReplay, filter, tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { concatLatestFrom } from '@ngrx/operators';
import { StrictlyProtectedCollectionTypes } from '../../../../../../shared/constants/strictly-protected-collections';
import { FormFieldType } from '../../../../../../../models/ts/form-field-type.model';
import { SearchFilterGroupDto } from '../../../../../../../models/ts/search-filter-group-dto.model';
import { OrganizationChartItemType } from '../../../../../../../models/ts/organization-chart-item-type.model';
import { StateType } from '../../../../../../../models/ts/state-type.model';
import { ConditionGroupType } from '../../../../../../../models/ts/condition-group-type.model';
import { FilterGroupType } from '../../../../../../../models/ts/filter-group-type.model';
import { Operators } from '../../../../../../../models/ts/operators.model';
import { SearchFilterFieldDto } from '../../../../../../../models/ts/search-filter-field-dto.model';
import { SearchFilterFieldValueBaseDto } from '../../../../../../../models/ts/search-filter-field-value-base-dto.model';
import { OrgChartItem } from '../../../../org-chart/interfaces/org-chart-item';
import { CollectionForm } from '../../../../../../../models/ts/collection-form.model';
import { CollectionFormField } from '../../../../../../../models/ts/collection-form-field.model';
import { TableFieldDataType } from '../../../../../../../models/ts/table-field-data-type.model';
import { ofType } from '@ngrx/effects';

@Component({
  selector: 'bizz-form-linked-collection-control',
  templateUrl: './form-linked-collection-control.component.html',
  styleUrls: ['./form-linked-collection-control.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LoaderComponent,
    FormCollectionListComponent
  ]
})
export class FormLinkedCollectionControlComponent extends BaseFormControlComponent implements OnInit {
  @ViewChild('formCollectionList') formCollectionList: FormCollectionListComponent;
  public openLinksInStack = true;
  public parentFilter: GridParentFilter | undefined;
  public parentFilterLoaded = false;
  public readOnly = computed(() => (this.formFieldSignal()?.IsReadOnly ?? false) || this.readOnlyOverride);
  public widgetData$: Observable<TableViewWidgetData> | undefined;
  public userReversedFixedGroupsFilter: SearchFilterGroupDto[] | undefined = undefined;

  @Output() public onWidgetDataLoaded = new EventEmitter<boolean>();
  private collectionListApiService = inject(CollectionListApiService);

  public override ngOnInit(): void {
    super.ngOnInit();
    this.listenForRefreshAction();
    const field = this.formFieldSignal();
    if (field) {
      this.store$.select(selectForm(this.formId)).pipe(
        takeUntilDestroyed(this.destroyRef),
        map((form) => {
          if (form) {
            if(field?.FormFieldType == FormFieldType.ReversedUserCollection){
              this.parentFilter = undefined;
              this.setUserReversedFixedGroupsFilter(form.data,field);
            }
             else {
              this.parentFilter = {
                CollectionsID: field.CollectionsID,
                CrosslinkCollectionsID: field.CrossLinkedCollectionsID,
                ChildInstancesID: form.data.InstancesID, // current instance
                //OriginalChildInstancesID: this.instancesId, // same as child because it's alwasy relational
                ChildVersionsID: form.data.VersionsID //  versionsId
              };
            }

            this.parentFilterLoaded = true;
          }
        })
      ).subscribe();

      this.widgetData$ = this.collectionListApiService.getListOptions(
        field.CollectionsID,
        field.ViewsID,
        field.ViewListsID
      ).pipe(
        concatLatestFrom(() => this.store$.select(selectForm(this.formId))),
        takeUntilDestroyed(this.destroyRef),
        map(([widgetData, form]) => {
          widgetData.ShowCaption = true;
          widgetData.Caption = field.Caption;
          widgetData.ListOptions.Records = field.ItemCount;
          widgetData.CanCreate = form !== undefined ? (form.data.InstancesID > 0 && !StrictlyProtectedCollectionTypes.includes(form.data.ProtectedCollectionType)) : false;
          return widgetData;
        }),
        shareReplay(1));
    }
  }

  public dataFetched (isLoaded: boolean): void {
    this.onWidgetDataLoaded.emit(isLoaded);
  }

  private listenForRefreshAction(): void {
    this.actions$.pipe(
      takeUntilDestroyed(this.destroyRef),
      ofType(refreshActions.refreshData),
      filter(props => props.collectionId == this.formFieldSignal()?.CollectionsID),
      tap(props => this.formCollectionList.fetchSearchData())
    ).subscribe();
  }

  private setUserReversedFixedGroupsFilter(form: CollectionForm, field: CollectionFormField): void {
    const selectedOrganizationChartItems: Array<OrgChartItem> = [];
    const orgItem = {} as OrgChartItem;
    orgItem.VersionId = form.VersionsID;
    orgItem.ItemType = OrganizationChartItemType.User;
    orgItem.State = StateType.Active;
    orgItem.Name = "Fixed user (reversed user collection)";

    selectedOrganizationChartItems.push(orgItem);
    const values: Array<SearchFilterFieldValueBaseDto> = [];
    const searchFilterFieldValueBaseDto: SearchFilterFieldValueBaseDto = {} as SearchFilterFieldValueBaseDto;
    searchFilterFieldValueBaseDto.SelectedOrganizationChartItems = selectedOrganizationChartItems;
    searchFilterFieldValueBaseDto.Operator = Operators.Contains;
    searchFilterFieldValueBaseDto.ViewDataSourcesID = field.ViewDataSourcesID;
    values.push(searchFilterFieldValueBaseDto);

    const fields : Array<SearchFilterFieldDto> = [];
    const searchFilterFieldDto : SearchFilterFieldDto = {} as SearchFilterFieldDto;
    searchFilterFieldDto.TableFieldID = field.CollectionFieldsID;
    searchFilterFieldDto.FieldType = TableFieldDataType.OrganizationChartUnitSelector;
    searchFilterFieldDto.Values = values;

    fields.push(searchFilterFieldDto);

    const group = {} as SearchFilterGroupDto;
    group.FilterType = FilterGroupType.Included;
    group.GroupType = ConditionGroupType.Fixed;
    group.Fields = fields;

    this.userReversedFixedGroupsFilter = [group];
  }

  protected override focus(): void {
    // Add your implementation here
  }
}
